// Utils
import Vector from '../../../../common/maths/geometry/Vector';
import { isAnnotation, isLine } from '../../../../common/elements/utils/elementTypeUtils';
import { getVectorMagnitude } from '../../../../common/maths/geometry/point';
import { isIconViewLike } from '../../../../common/elements/utils/elementDisplayUtils';

export const NO_ROTATION = { x: 0, y: 0 };
export const STANDARD_FRAME_TIME_MS = 1000 / 60;

global.dnd = {
    shadowMaxDist: 50,
    shadowGrowRate: 0.05,
    shadowGrowRateTouch: 0.1,
    shadowFadeRate: 0.1,

    lerp_ratio_3d: 0.5,
    lerp_ratio_default: 0.6,
};

/**
 * Don't rotate elements if 3D effects are disabled, there's more than one element being dragged, or
 * the element being dragged is a board or a line.
 */
export const getShouldRotateElements = ({ areDnd3dEffectsEnabled, element, elementCount }) => {
    if (!areDnd3dEffectsEnabled) return false;
    if (elementCount > 1) return false;

    return !isIconViewLike(element) && !isLine(element) && !isAnnotation(element);
};

/**
 * When dragging elements that aren't rotated, don't make them lag the cursor by as much, so they feel
 * snappier.  The smaller lerp is used for the rotated elements because it makes the rotation animation
 * smoother.
 */
const getPositionLinearInterpolationRatio = (shouldRotateElements) =>
    shouldRotateElements ? global.dnd.lerp_ratio_3d || 0.3 : global.dnd.lerp_ratio_default || 0.6;

/**
 * Determines the amount to change the element's position.
 * This uses the change in time to standardise the movement and improve the smoothing of the animation.
 *
 * For example:
 *  - Usually a frame takes 16ms, so we want to draw 0.3 * the difference between the current position
 *      and the target position every frame.
 *  - If the last frame took 32ms to render then we want to skip a frame, so we want to draw 2 times
 *      the difference.
 *  - Thus the calculation is: position difference * (32 / 16) = position difference * 2.
 */
export const calculatePositionDelta = ({
    targetPosition,
    previousPosition,
    shouldRotateElements,
    dt = STANDARD_FRAME_TIME_MS,
}) => {
    if (!previousPosition) return targetPosition;

    const positionInterpolationRatio = getPositionLinearInterpolationRatio(shouldRotateElements);

    return {
        x:
            (targetPosition.x - previousPosition.x) *
            // Use a max of 1 to ensure that the position doesn't get over-shot
            Math.min((positionInterpolationRatio * dt) / STANDARD_FRAME_TIME_MS, 1),
        y:
            (targetPosition.y - previousPosition.y) *
            // Use a max of 1 to ensure that the position doesn't get over-shot
            Math.min((positionInterpolationRatio * dt) / STANDARD_FRAME_TIME_MS, 1),
    };
};

/**
 * Determines the new rotation of the element based on the change in position, the previous rotation
 * and maximum allowed rotation for the element size.
 */
export const calculateRotation = ({ positionDelta, previousRotation, damping = 0.2, maxRotation = 25 }) => {
    // NOTE: The 10 values here are arbitrary, based on how it felt, however the maximum rotation is
    //  used here to ensure the target rotation is smaller for larger elements.
    //  Larger elements will have a smaller maximum rotation, and thus the amount to rotate them by based on
    //  each change in position should be less.
    const targetRotationVector = new Vector(
        // X rotations depend on the change in Y
        -positionDelta.y * (maxRotation / 10),
        // Y rotations depend on the change in X
        positionDelta.x * (maxRotation / 10),
    );

    // The rotation in both directions should not exceed a maximum combined amount, otherwise
    // diagonal rotations look weird
    targetRotationVector.clampLength(maxRotation);

    // Linear interpolation - get the difference of the rotation and apply a damping to it to smooth out changes
    const rotationDiff = targetRotationVector.subtract(previousRotation);
    rotationDiff.multiplyScalar(damping);

    const rotation = rotationDiff.add(previousRotation);

    return rotation.toObject();
};

/**
 * Determines whether the animation needs to continue because the current change in position or rotation
 * is not small enough to ignore.
 */
export const getHasOutstandingChanges = (rotation, positionDelta) =>
    Math.abs(rotation.x) > 0.5 ||
    Math.abs(rotation.y) > 0.5 ||
    Math.abs(positionDelta.x) > 1 ||
    Math.abs(positionDelta.y) > 1;

export const getDesiredShadowIntensity = (currentShadowIntensity, movementOffset, isTouchDevice) => {
    if (isTouchDevice) return 1;

    return currentShadowIntensity >= 1
        ? 1
        : Math.max(currentShadowIntensity, getVectorMagnitude(movementOffset) / global.dnd.shadowMaxDist);
};

export const getNextShadowIntensityGrowing = (currentShadowIntensity, desiredShadowIntensity, isTouchDevice) => {
    if (currentShadowIntensity >= 1) return 1;

    const growRate = isTouchDevice ? global.dnd.shadowGrowRateTouch : global.dnd.shadowGrowRate;

    return Math.min(currentShadowIntensity + growRate, 1, desiredShadowIntensity);
};

export const getNextShadowIntensityFading = (currentShadowIntensity) =>
    Math.max(currentShadowIntensity - global.dnd.shadowFadeRate, 0);
